'use client';

import type { CSSProperties, ImgHTMLAttributes, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_IMAGE } from '@/app/components/media/image/constants';
import { PreloadImage } from '@/app/components/media/image/preload-image';
import { Sources } from '@/app/components/media/image/sources';
import { ImageStyled, PictureStyled } from '@/app/components/media/image/styles';
import type { ImageProps, ImageRenderOption } from '@/app/components/media/image/types';
import { getAdjustedImageSrc, getImageQueryParams, getImgSrcset } from '@/app/components/media/image/utils';
import { useInView } from '@/app/hooks/useInView';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { isUnprocessableImageUrl } from '@/app/utils/image';
import { safeCall } from '@/app/utils/safeCall';
export const Image = ({
  src,
  alt,
  width,
  height,
  sources = [],
  placeholder,
  loading = 'lazy',
  loadedEffect = 'fade',
  priority = false,
  layout = 'ratio',
  hoverEffect,
  rootRef = null,
  rootMargin = '0px 0px 0px 0px',
  srcSetOptions = {},
  sizes = '100vw',
  className = '',
  imageClassName = '',
  aspectRatio,
  objectFit,
  objectPosition,
  onLoad,
  isGroupHover,
  attributes = {},
  crossOrigin
}: ImageProps) => {
  // Adjust loading behavior if the image is marked as priority
  if (priority) {
    loading = 'eager';
    loadedEffect = null;
    placeholder = undefined;
  }
  const pictureRef = useRef<HTMLPictureElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(priority);
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(!!placeholder);
  const inView = useInView(pictureRef, {
    root: rootRef,
    rootMargin,
    threshold: 0.01
  });
  const isUnprocessableSrc = isUnprocessableImageUrl(src);
  const renderOption: ImageRenderOption = sources.length > 0 ? 'sources' : 'sizes';
  const handleImageLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    if (event.currentTarget?.src === DEFAULT_IMAGE) return;
    setIsLoaded(true);
    if (showPlaceholder) {
      setShowPlaceholder(false);
    }
    safeCall(onLoad, event.currentTarget);
  }, [onLoad, showPlaceholder]);
  const memoizedSrc = useMemo(() => getAdjustedImageSrc(getImageQueryParams(src, srcSetOptions, renderOption), DEFAULT_IMAGE, inView, loading), [src, inView, loading, srcSetOptions, renderOption]);
  const memoizedSrcSet = useMemo(() => {
    return getImgSrcset(src, srcSetOptions, inView, loading);
  }, [src, srcSetOptions, inView, loading]);
  const imgSrcsetProps = useMemo<ImgHTMLAttributes<HTMLImageElement>>(() => ({
    srcSet: isUnprocessableSrc ? undefined : memoizedSrcSet,
    sizes: isUnprocessableSrc ? undefined : sizes
  }), [memoizedSrcSet, isUnprocessableSrc, sizes]);
  useEffect(() => {
    /**
     * Note: Fix image onLoad event not fired when image is already loaded in priority
     */
    if (!!priority && imgRef.current?.complete && typeof onLoad === 'function') {
      safeCall(onLoad, imgRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!src) {
    return null;
  }
  return <>
      {!!priority && <PreloadImage src={src} sources={sources} srcSetOptions={srcSetOptions} sizes={sizes} />}
      <picture ref={pictureRef} className={PictureStyled({
      layout,
      placeholder,
      showPlaceholder,
      className: cn(className, aspectRatio !== 'calculate' && aspectRatio,
      // aspectRatio can be array, so it needs to write this way
      {
        'calc-ratio': aspectRatio === 'calculate'
      })
    })} {...attributes} style={aspectRatio === 'calculate' ? {
      '--aspect-ratio': `${width} / ${height}`,
      ...(attributes?.style ?? {})
    } as CSSProperties : attributes?.style}>
        {renderOption === 'sources' && <Sources loading={loading} inView={inView} sources={sources} />}
        <img ref={imgRef} {...createDataAttributes({
        test: 'image'
      })} src={memoizedSrc} alt={alt} width={width} height={height} loading={loading} {...renderOption === 'sizes' ? imgSrcsetProps : {}} className={ImageStyled({
        isLoaded,
        loadedEffect: loading === 'eager' ? 'none' : loadedEffect,
        hoverEffect,
        layout,
        objectFit,
        objectPosition,
        isGroupHover,
        className: imageClassName
      })} crossOrigin={crossOrigin} onLoad={handleImageLoad} />
      </picture>
    </>;
};